import { useCallback, useEffect, useRef, useState } from "react";
import {
  AnimatePresence,
  motion,
  useInView,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
  PanInfo,
} from "framer-motion";
import { ArrowLeft, ArrowRight } from "@phosphor-icons/react";
import { Project, Shot } from "../data/projects";
import s from "./PhoneShowcase.module.css";

const EASE = [0.16, 1, 0.3, 1] as const;
const SPRING = { stiffness: 140, damping: 20 };

type Frame = "phone" | "desktop";

/** Pointer-driven tilt held in motion values, so nothing re-renders while the pointer moves. */
function useTilt(range: number) {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [range, -range]), SPRING);
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-range, range]), SPRING);
  const onMove = (e: React.PointerEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return { rx, ry, onMove, reset };
}

function Device({ children, label, frame = "phone" }: { children: React.ReactNode; label: string; frame?: Frame }) {
  const tilt = useTilt(frame === "desktop" ? 4 : 7);
  if (frame === "desktop") {
    return (
      <div className={s.desktopWrap}>
        <motion.div
          className={s.desktop}
          style={{ rotateX: tilt.rx, rotateY: tilt.ry, transformPerspective: 1400 }}
          onPointerMove={tilt.onMove}
          onPointerLeave={tilt.reset}
        >
          <div className={s.chrome} aria-hidden="true">
            <span />
            <span />
            <span />
          </div>
          <div className={s.desktopScreen}>{children}</div>
        </motion.div>
        <p className={s.label}>{label}</p>
      </div>
    );
  }
  return (
    <div className={s.deviceWrap}>
      <motion.div
        className={s.device}
        style={{ rotateX: tilt.rx, rotateY: tilt.ry, transformPerspective: 1200 }}
        onPointerMove={tilt.onMove}
        onPointerLeave={tilt.reset}
      >
        <span className={s.island} aria-hidden="true" />
        <div className={s.screen}>{children}</div>
      </motion.div>
      <p className={s.label}>{label}</p>
    </div>
  );
}

interface CarouselProps {
  shots: Shot[];
  name: string;
  frame?: Frame;
  labelPrefix?: string;
  interval?: number;
}

function Carousel({ shots, name, frame = "phone", labelPrefix, interval = 3800 }: CarouselProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [[index, dir], setState] = useState<[number, number]>([0, 1]);
  const [paused, setPaused] = useState(false);
  const reduce = useReducedMotion();
  const inView = useInView(ref, { amount: 0.5 });

  const go = useCallback(
    (delta: number) => setState(([i]) => [(i + delta + shots.length) % shots.length, delta > 0 ? 1 : -1]),
    [shots.length]
  );

  // Advance only while visible, so stacked cards do not animate off screen.
  useEffect(() => {
    if (paused || reduce || !inView) return;
    const id = window.setInterval(() => go(1), interval);
    return () => window.clearInterval(id);
  }, [paused, reduce, inView, go, interval]);

  const shot = shots[index];
  const label = [labelPrefix, shot.caption].filter(Boolean).join(": ");
  const variants = {
    enter: (d: number) => ({ x: reduce ? 0 : `${d * 14}%`, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: reduce ? 0 : `${d * -14}%`, opacity: 0 }),
  };

  return (
    <div
      ref={ref}
      className={s.carousel}
      role="region"
      aria-roledescription="carousel"
      aria-label={`${name} screens`}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") go(1);
        if (e.key === "ArrowLeft") go(-1);
      }}
    >
      <Device frame={frame} label={label}>
        <AnimatePresence initial={false} custom={dir} mode="popLayout">
          <motion.img
            key={shot.src}
            src={shot.src}
            alt={shot.alt}
            className={s.slide}
            width={frame === "desktop" ? 1600 : 900}
            height={frame === "desktop" ? 1000 : 1956}
            loading="lazy"
            custom={dir}
            variants={variants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ duration: 0.55, ease: EASE, opacity: { duration: 0.35 } }}
            drag={reduce ? false : "x"}
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.12}
            onDragEnd={(_: unknown, info: PanInfo) => {
              if (info.offset.x < -40) go(1);
              else if (info.offset.x > 40) go(-1);
            }}
          />
        </AnimatePresence>
      </Device>

      <div className={s.controls}>
        <button type="button" className={s.arrow} onClick={() => go(-1)} aria-label="Previous screen">
          <ArrowLeft size={16} weight="bold" />
        </button>
        <div className={s.dots} role="tablist" aria-label="Choose screen">
          {shots.map((sh, i) => (
            <button
              key={sh.src}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={sh.caption || `Screen ${i + 1}`}
              className={`${s.dot} ${i === index ? s.dotActive : ""}`}
              onClick={() => setState([i, i > index ? 1 : -1])}
            />
          ))}
        </div>
        <button type="button" className={s.arrow} onClick={() => go(1)} aria-label="Next screen">
          <ArrowRight size={16} weight="bold" />
        </button>
      </div>
      <p className="visually-hidden" aria-live="polite">
        {`Screen ${index + 1} of ${shots.length}${shot.caption ? `: ${shot.caption}` : ""}`}
      </p>
    </div>
  );
}

/** Framed carousels: an optional browser carousel, an optional "before" phone, and a phone carousel. */
export default function PhoneShowcase({ project }: { project: Project }) {
  const hasDesktop = !!project.desktopImages?.length;
  const phonePrefix = project.before ? "After" : hasDesktop ? "App" : undefined;
  return (
    <div className={`${s.showcase} ${hasDesktop ? s.withDesktop : ""}`}>
      {hasDesktop && (
        <Carousel
          shots={project.desktopImages!}
          name={`${project.name} dashboard`}
          frame="desktop"
          labelPrefix={project.images.length ? "Dashboard" : undefined}
          interval={4600}
        />
      )}
      {project.before && (
        <div className={s.before}>
          <Device label={project.before.caption || "Before"}>
            <img
              src={project.before.src}
              alt={project.before.alt}
              className={s.slide}
              width={736}
              height={1600}
              loading="lazy"
            />
          </Device>
        </div>
      )}
      {project.images.length > 0 && <Carousel shots={project.images} name={project.name} labelPrefix={phonePrefix} />}
    </div>
  );
}

/** Three static phones fanned out, tilting together toward the pointer. */
export function PhoneFan({ project }: { project: Project }) {
  const tilt = useTilt(7);
  return (
    <motion.div
      className={s.fan}
      style={{ rotateX: tilt.rx, rotateY: tilt.ry }}
      onPointerMove={tilt.onMove}
      onPointerLeave={tilt.reset}
    >
      {project.images.slice(0, 3).map((img, i) => (
        <motion.figure
          key={img.src}
          className={s.fanPhone}
          data-index={i}
          whileHover={{ y: -12 }}
          transition={{ type: "spring", stiffness: 160, damping: 22 }}
        >
          <img src={img.src} alt={img.alt} loading="lazy" width={720} height={1560} />
        </motion.figure>
      ))}
    </motion.div>
  );
}
